import { useState, type FormEvent } from "react";
import { Trash2 } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Badge, Button, Dialog, Field, Input } from "@/components/ui/primitives";
import { useCreateDepartment, useDeleteDepartment, useDepartments } from "./hooks";

/** Create / list / delete departments. Gated by the caller on settings:manage. */
export function DepartmentsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: departments } = useDepartments();
  const create = useCreateDepartment();
  const del = useDeleteDepartment();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  async function add(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({ name: name.trim(), description: description.trim() || null });
      setName("");
      setDescription("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create the department.");
    }
  }

  async function remove(id: string) {
    setError(null);
    try {
      await del.mutateAsync(id);
      setConfirmId(null);
    } catch (err) {
      setConfirmId(null);
      setError(err instanceof ApiError ? err.message : "Could not delete the department.");
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Departments">
      <div className="space-y-5">
        {error && (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}

        <form onSubmit={add} className="space-y-3" noValidate>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Name" htmlFor="dept_name">
              <Input
                id="dept_name"
                required
                minLength={2}
                placeholder="e.g. Field Operations"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field label="Description (optional)" htmlFor="dept_desc">
              <Input
                id="dept_desc"
                placeholder="What this team handles"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={create.isPending || name.trim().length < 2}>
              {create.isPending ? "Adding…" : "Add department"}
            </Button>
          </div>
        </form>

        <div className="rounded-md border border-border">
          <div className="border-b border-border bg-muted/40 px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Existing departments
          </div>
          <ul className="divide-y divide-border">
            {(departments ?? []).length === 0 && (
              <li className="px-3 py-4 text-sm text-muted-foreground">No departments yet.</li>
            )}
            {(departments ?? []).map((d) => (
              <li key={d.id} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{d.name}</div>
                  {d.description && (
                    <div className="truncate text-xs text-muted-foreground">{d.description}</div>
                  )}
                </div>
                <Badge tone="muted">
                  {d.user_count} user{d.user_count === 1 ? "" : "s"}
                </Badge>
                {confirmId === d.id ? (
                  <span className="flex shrink-0 items-center gap-2">
                    <span className="text-xs text-muted-foreground">Delete?</span>
                    <Button
                      variant="destructive"
                      disabled={del.isPending}
                      onClick={() => void remove(d.id)}
                    >
                      Yes
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirmId(null)}>
                      No
                    </Button>
                  </span>
                ) : (
                  <button
                    type="button"
                    aria-label={`Delete ${d.name}`}
                    title={
                      d.user_count > 0 ? "Reassign its users before deleting" : "Delete department"
                    }
                    className="shrink-0 rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-destructive"
                    onClick={() => setConfirmId(d.id)}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Dialog>
  );
}
